// The viewport `layer` commands a loaded sim turns on, by contract: hemo vessels and particles (or a
// tissue-fate volume), dynamics cortical activity, cells arbors, pharma's engagement surface. Names
// follow viewport-api-v0 and cells-sim-v0.

import type { Command } from '../../viewport/api.ts';
import { colormap } from '../../ui/scales.ts';
import type { Overlay } from './overlay.ts';
import type { CellsHeader, CommonHeader, DynamicsHeader, PharmaHeader, SimFormat } from './types.ts';

export type LayerCommand = Extract<Command, { cmd: 'layer' }>;

/** Particles drawn along the vessels of a 1D network by default (the engine may cap it). */
export const PARTICLE_COUNT = 12000;

/** The most particles the Particles section's density asks for: at its default 60 %, PARTICLE_COUNT. */
export const PARTICLE_MAX = 20000;

/** How many particles a density (a share of PARTICLE_MAX) asks for. */
export const particleCount = (density: number) => Math.round(PARTICLE_MAX * Math.min(1, Math.max(0, density)));

/** Membrane voltage on the activity ramp, mV: rest sits near its floor, dendritic plateaus go amber, spikes white. */
export const MEMBRANE_MV: [number, number] = [-80, 0];

const stem = (p: string) => /([^/]+?)(\.[^./]+)?$/.exec(p)?.[1] ?? p;

export function layersFor(slot: string, format: SimFormat, header: CommonHeader, overlay?: Overlay): LayerCommand[] {
  switch (format) {
    case 'hemo': {
      if (header.kind === '1d-network') {
        const q = overlay?.quantity ?? 'velocity';
        return [
          { cmd: 'layer', name: 'vessels', on: true, slot, quantity: q, colormap: colormap(overlay?.ramp ?? 'velocity'), ...(overlay ? { range: overlay.range } : {}) },
          { cmd: 'layer', name: 'particles', on: true, slot, count: PARTICLE_COUNT },
        ];
      }
      const vols = ((header.files?.volumes as string[] | undefined) ?? []).map(stem);
      const state = vols.find((v) => v === 'tissue-state') ?? vols.find((v) => /cbf|tmax|p-infarct/.test(v));
      return state ? [{ cmd: 'layer', name: `volume:${state}`, on: true, slot }] : [];
    }
    case 'dynamics': {
      const range = (header as DynamicsHeader).display?.activity_range ?? overlay?.range;
      // A spreading depolarization on design's cold scale, so it reads apart from a seizure's ember.
      const ramp = header.kind === 'spreading-depolarization' ? 'sd' : 'activity';
      return [{ cmd: 'layer', name: 'activity', on: true, slot, colormap: colormap(ramp), ...(range ? { range } : {}) }];
    }
    case 'cells': {
      const v = (header as CellsHeader).series?.find((s) => s.quantity === 'v');
      const range = v && (v.units ?? 'mV') === 'mV' ? MEMBRANE_MV : v?.range;
      return [
        { cmd: 'layer', name: 'arbors', on: true, slot, quantity: 'v', colormap: colormap('activity'), ...(range ? { range } : {}) },
        { cmd: 'layer', name: 'somata', on: true, slot },
        ...(typeof header.files?.synapses === 'string' && header.files.synapses ? [{ cmd: 'layer' as const, name: 'synapses', on: true, slot }] : []),
      ];
    }
    case 'pharma': {
      const surfaces = (header as PharmaHeader).files?.surfaces ?? [];
      return surfaces.map((p) => ({ cmd: 'layer' as const, name: `surface:${stem(p)}`, on: true, slot, quantity: 'engagement', colormap: colormap('occupancy'), range: [0, 1] as [number, number] }));
    }
  }
}
